import { skipToken } from "@reduxjs/toolkit/query";
import { getCompanyTimezone } from "@repo/app-types/entities/company";
import {
  checkPlaybookHasKnowledgeBaseAdherence,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import { TeammateType } from "@repo/app-types/entities/teammate";
import { asGradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import {
  computeAggregatedAdherenceScore,
  computeGradingTalkRatio,
} from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { isAlpharunEmail } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { IconRefresh, IconUser } from "@tabler/icons-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { Header, Heading } from "react-aria-components";
import { useParams } from "react-router";
import {
  usePlaybookGradingQuery,
  usePlaybookGradingReassessMutation,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useMyRecordingAssessmentFeedbackQuery } from "../../api/entities/recording.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { useAppDispatch, useAppSelector } from "../../hooks/hook.js";
import { NavigationSlideoverContainer } from "../../hooks/useModalNavigation.js";
import { selectCompany } from "../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { CallActivityEmptyState } from "./callActivityEmptyState.js";
import { GradingAssessmentTable } from "./gradingAssessmentTable.js";
import {
  type CustomerExperienceScoreValue,
  GradingCustomerExperienceScorePill,
} from "./gradingCustomerExperienceScorePill.js";
import { GradingCustomerSidebar } from "./gradingCustomerSidebar.js";
import { GradingPassRate } from "./gradingPassRate.js";
import { GradingScheduledCallBanner } from "./gradingScheduledCallBanner.js";
import { GradingTalkRatio } from "./gradingTalkRatio.js";
import { IncorrectStatementsIndication } from "./incorrectStatementsIndication.js";
import { KnowledgeBaseAdherenceStatementsCardContainer } from "./knowledgeBaseAdherenceStatementsCardContainer.js";
import { OutcomeValuePill } from "./outcomeValuePill.js";
import { renderTeammateName } from "./teammateHelpers.js";
import { ToolUsageAccuracyCardContainer } from "./toolUsageAccuracyCardContainer.js";

/**
 * Page accessible via https://<host>/performance/playbooks/<playbookId>/customer-interactions/<gradingId>/assessment
 */
export const TrainingTeammatePageOverviewGradingAssessment: FC = () => {
  const { gradingId: gradingIdParam } = useParams();
  if (!gradingIdParam) {
    throw new Error("Missing params.");
  }
  const gradingId = asGradingId(gradingIdParam);

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId });

  // teammate/account/playbook are derived from the grading; the route only
  // carries the grading id.
  const teammateId = grading?.teammate_id;
  const playbookId = grading?.playbook_id;

  const { data: teammate } = useBasicTeammateQuery(teammateId ?? skipToken);

  const assessment = grading?.assessment;
  const callActivity = grading?.call_activity;
  const firstContact = grading?.contacts.at(0);
  const firstRecording = grading?.recordings.at(0);
  const companyTimezone = getCompanyTimezone(useAppSelector(selectCompany));

  const [reassessGrading, { isLoading: isReassessing }] =
    usePlaybookGradingReassessMutation();
  const [justUpdated, setJustUpdated] = useState(false);

  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  const { data: feedback } = useMyRecordingAssessmentFeedbackQuery(
    firstRecording?.id && playbook?.id
      ? {
          recordingId: firstRecording.id,
          playbookId: playbook.id,
        }
      : skipToken,
  );

  const scoreDetails = grading?.score_details;

  // Hide the "Updated" indicator after 3 seconds
  useEffect(() => {
    if (!justUpdated) {
      return;
    }

    const timer = setTimeout(() => setJustUpdated(false), 3000);
    return () => clearTimeout(timer);
  }, [justUpdated]);

  const dispatch = useAppDispatch();
  const handleReassess = async () => {
    if (!grading) {
      return;
    }

    try {
      await reassessGrading({ gradingId }).unwrap();
      setJustUpdated(true);
    } catch (error) {
      showNotification(dispatch, {
        id: `reassess-error-${grading.id}-${Date.now()}`,
        type: NotificationType.FAILURE,
        primaryMessage: "Failed to regrade recording. Please try again.",
      });
    }
  };

  const ownTeammate = useAppSelector(selectTeammate) || undefined;
  const maybeRenderReassessButton = () => {
    if (!isAlpharunEmail(ownTeammate?.email)) {
      return null;
    }

    return (
      <div className="flex items-center gap-2">
        {justUpdated && (
          <span className="animate-pulse rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
            Updated
          </span>
        )}
        <Button
          variant={ButtonVariant.Tertiary}
          size={Size.MEDIUM}
          onClick={handleReassess}
          isDisabled={isReassessing}
        >
          {isReassessing ? (
            <div className="flex flex-1 items-center pt-px">
              <Loader style={LoaderStyle.ORBIT} />
            </div>
          ) : (
            <>
              <IconRefresh className="size-4" />
              <VisuallyHidden>Reassess</VisuallyHidden>
            </>
          )}
        </Button>
      </div>
    );
  };

  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);
  const talkRatio = computeGradingTalkRatio(
    scoreDetails?.talk_ratio?.speaker_ms,
    scoreDetails?.talk_ratio?.total_speech_ms,
  );
  // Voice agents are graded on their own playbook, so the playbook adherence
  // results aren't meaningful for them — hide that section for agent calls.
  const isVoiceAgent = teammate?.type === TeammateType.AGENT;
  const shouldShowTalkRatio = !isVoiceAgent && talkRatio !== undefined;

  const isCustomerExperienceScoreEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.CustomerExperienceScore,
  );
  const customerExperienceScore = grading?.customer_experience_score ?? null;
  const shouldShowCustomerExperienceScore =
    isCustomerExperienceScoreEnabled && customerExperienceScore !== null;
  const shouldShowOutcome =
    outcomeDefinition !== undefined && !isGradingLoading;

  return (
    <>
      <div className="flex h-full">
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="space-y-6 px-6 py-4">
            {callActivity?.next_action && grading ? (
              <GradingScheduledCallBanner
                nextAction={callActivity.next_action}
                gradingId={grading.id}
                teammateAgentId={grading.teammate_id}
                customerName={firstContact?.first_name ?? undefined}
                timezone={companyTimezone}
              />
            ) : null}

            {/* Only when the tab would otherwise hold nothing but the banner
                above — everything else here hangs off the assessment. */}
            {!assessment ? (
              <CallActivityEmptyState
                callActivity={callActivity}
                surface="overview"
              />
            ) : null}

            {assessment ? (
              <>
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
                    <div className="flex min-w-0 items-center gap-1">
                      <IconUser className="size-4 shrink-0" />
                      <span className="truncate">
                        {renderTeammateName(teammate)}
                      </span>
                      {/* TODO: Remove this once we have a better way to regrade recordings */}
                      {maybeRenderReassessButton()}
                    </div>

                    <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2">
                      {shouldShowOutcome ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">
                            Outcome
                          </span>
                          <OutcomeValuePill
                            outcomeDefinition={outcomeDefinition}
                            outcome={grading?.outcome}
                            size={Size.SMALL}
                          />
                        </div>
                      ) : null}

                      {shouldShowCustomerExperienceScore ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">
                            CX score
                          </span>
                          <GradingCustomerExperienceScorePill
                            score={
                              customerExperienceScore as CustomerExperienceScoreValue
                            }
                          />
                        </div>
                      ) : null}

                      {shouldShowTalkRatio ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-foreground">
                            Talk ratio
                          </span>
                          <GradingTalkRatio
                            size={Size.SMALL}
                            speakerMs={scoreDetails?.talk_ratio?.speaker_ms}
                            totalSpeechMs={
                              scoreDetails?.talk_ratio?.total_speech_ms
                            }
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                {grading.tool_usage_accuracy ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Tool usage accuracy
                      </Heading>
                    </Header>

                    <ToolUsageAccuracyCardContainer
                      data={grading.tool_usage_accuracy}
                      gradingId={gradingId}
                    />
                  </section>
                ) : null}

                {!isVoiceAgent ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        {playbook?.name ?? "Playbook adherence"}
                      </Heading>
                      <GradingPassRate
                        size={Size.SMALL}
                        passRate={
                          assessment && playbook
                            ? (computeAggregatedAdherenceScore(
                                assessment,
                                playbook.assessment_tree,
                              ) ?? undefined)
                            : undefined
                        }
                        isLoading={isGradingLoading}
                      />
                    </Header>

                    {playbook && firstRecording && (
                      <GradingAssessmentTable
                        assessment={assessment}
                        recordingId={firstRecording.id}
                        playbook={playbook}
                        feedback={feedback}
                      />
                    )}
                  </section>
                ) : null}

                {playbook !== undefined &&
                checkPlaybookHasKnowledgeBaseAdherence(playbook) ? (
                  <section className="space-y-4">
                    <Header className="flex items-center gap-3">
                      <Heading
                        level={4}
                        className="text-base font-semibold text-foreground"
                      >
                        Knowledge base adherence
                      </Heading>

                      <IncorrectStatementsIndication
                        isVisibleWhenZero
                        knowledgeBaseAdherence={
                          grading.knowledge_base_adherence
                        }
                      />
                    </Header>

                    <KnowledgeBaseAdherenceStatementsCardContainer
                      recordings={grading.recordings}
                      knowledgeBaseAdherence={grading.knowledge_base_adherence}
                    />
                  </section>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {firstContact && (
          <GradingCustomerSidebar
            gradingId={gradingId}
            contact={firstContact}
          />
        )}
      </div>
      <NavigationSlideoverContainer />
    </>
  );
};
